import type React from "react";
import { SetupCard } from "@fas/ui-core";
import {
  FormControl,
  InputAdornment,
  TextField,
  Tooltip,
  Box,
  FormHelperText,
  CircularProgress,
  Grid,
} from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Autocomplete from "@mui/material/Autocomplete";
import type { ErrorsMapper } from "@fas/ui-framework";
import { hasError, getErrorMessage } from "@fas/ui-framework";
import type {
  Settings as SettingsInterface,
  Sorting,
  Algorithm,
} from "../types/LinkingInfoForm.types";

export type Props = SettingsInterface & {
  onSortingChange: (value: string) => void;
  onWidthChange: (value: string) => void;
  onHeightChange?: (value: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onAlgorithmChange: (value: string) => void;
  errors: ErrorsMapper;
  isHeightLoading: boolean;
  sortings: Array<Sorting>;
  algorithms: Array<Algorithm>;
};

function Settings({
  algorithm,
  errors,
  height,
  onAlgorithmChange,
  onHeightChange,
  onSortingChange,
  onWidthChange,
  sorting,
  width,
  type,
  isHeightLoading,
  sortings,
  algorithms,
}: Props): React.ReactElement {
  return (
    <SetupCard title="Settings">
      <Grid container columnSpacing={2} width={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            fullWidth
            error={hasError("sorting", errors)}
            data-testid="sorting"
          >
            <Autocomplete
              id="sorting"
              options={sortings}
              value={sorting}
              size="small"
              onChange={(_, value) => onSortingChange(value || "")}
              renderInput={(params): React.ReactElement<TextFieldProps> => (
                <TextField
                  {...params}
                  label="Sorting"
                  variant="outlined"
                  margin="normal"
                  error={hasError("sorting", errors)}
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": "sorting",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip title="You can sort pages for this linking. Linking pages will be sorted by date of
                            last publication. If you want to update sorting, you need to republish your linking."
                        >
                          <HelpOutlineIcon />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Box ml={2}>
              <FormHelperText>
                {getErrorMessage("sorting", errors)}
              </FormHelperText>
            </Box>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            fullWidth
            error={hasError("algorithm", errors)}
            data-testid="algorithm"
          >
            <Autocomplete
              id="algorithm"
              options={algorithms}
              disableClearable
              disabled={type === "matrix"}
              value={algorithm}
              size="small"
              onChange={(_, value) => onAlgorithmChange(value || "")}
              renderInput={(params): React.ReactElement<TextFieldProps> => (
                <TextField
                  {...params}
                  label="Algorithm"
                  variant="outlined"
                  margin="normal"
                  error={hasError("algorithm", errors)}
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": "algorithm",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip title="The circle is the default algorithm. For example, you link pages 1,2,3.
                            On page 1 you can see 2, 3 pages. On page 2 you can see 3, 1 pages. On page 3 you can see 1, 2 pages."
                        >
                          <HelpOutlineIcon />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Box ml={2}>
              <FormHelperText>
                {getErrorMessage("algorithm", errors)}
              </FormHelperText>
            </Box>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={hasError("width", errors)}>
            <TextField
              label="Width"
              variant="outlined"
              margin="normal"
              value={width}
              inputProps={{
                min: "0",
                step: "1",
                "data-testid": "width",
              }}
              size="small"
              error={hasError("width", errors)}
              onChange={({ target: { value } }) => onWidthChange(value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="Enter the appropriate count of pages with the &quot;Generation Tags&quot;,
                      which will be displayed on each page."
                    >
                      <HelpOutlineIcon />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Box ml={2}>
              <FormHelperText>{getErrorMessage("width", errors)}</FormHelperText>
            </Box>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <TextField
              label="Height"
              variant="outlined"
              margin="normal"
              type="number"
              disabled
              inputProps={{
                min: "0",
                step: "1",
                "data-testid": "height",
              }}
              size="small"
              value={height}
              error={hasError("height", errors)}
              onChange={(event) => onHeightChange && onHeightChange(event)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="This parameter will be auto-generated. It shows the actual
                      count of pages with the selected &quot;Generation Tags&quot; in the system."
                    >
                      <HelpOutlineIcon />
                    </Tooltip>
                  </InputAdornment>
                ),
                endAdornment: (isHeightLoading && <CircularProgress color="inherit" size={20} />),
              }}
            />
            <Box ml={2}>
              <FormHelperText />
            </Box>
          </FormControl>
        </Grid>
      </Grid>
    </SetupCard>
  );
}

export default Settings;
